import React, { forwardRef, useEffect, useImperativeHandle, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import QS from 'query-string';
import { useAntLocale, useAntModal, useDeepCompareEffect } from '@fuxi/eevee-hooks';
import { Modal } from '@fuxi/eevee-ui';
import {
  LoginType,
  UrsOptions,
  setNeteaseCookieDomain,
  initUrs,
  EN,
  URS_PAGE_TYPE_SEARCH_KEY,
  UrsPageType,
  SubPackageSlugEnum,
} from '@yaotai/common-utils';
import { EmailLogin, PhoneLogin } from '@yaotai/components';
import { encryptPassword } from '../../utils/encryptPassword';
import { encodeNextUrl } from '../../utils';
import API from '../../services';

import {
  GOOGLE_LOGIN_ICON,
  LoginRegisterType,
  LoginTypeEnum,
  MailRegisterType,
  NEED_AUTO_REGISTER,
  OTHER_LOGIN_ICON,
  WECHAT_LOGIN_ICON,
  YAOTAI_LOGO_BLACK,
  YAOTAI_LOGO_BLACK_EN,
  useInvokeAreaPrefix,
  useMatchAid,
} from '../../../';
import { ThirdLogin } from '../../components';
import { useYaotaiGlobalContextState } from '../../contexts';
import { LoginCBOption, titleMap } from '../../constants';
import { useLoginType } from '../../hooks';

import { UrsModalLayout } from './styles';

const URS_ID = 'urs-modal-include';

export type AutoRegisterFlagRefCurrent = {
  flag: boolean;
  option?: `${LoginCBOption}`;
};

export const UrsModal = forwardRef<
  ModalRef,
  {
    autoRegisterFlagRef?: React.MutableRefObject<AutoRegisterFlagRefCurrent>;
    cookieDomain: string;
    options: UrsOptions;
    overseaRbacUrl: string;
    rbacServer: string;
    rbacUrl: string;
  }
>(({ autoRegisterFlagRef, options, cookieDomain, rbacServer, rbacUrl, overseaRbacUrl }, ref) => {
  const { showModal, commonAntModalProps, closeModal } = useAntModal({
    handleCancelCallback: () => {
      searchParams.has('from') && searchParams.delete('from');
      navigate(
        {
          search: searchParams.toString(),
        },

        { replace: true },
      );
    },
  });
  const { oem, isMainLogoOemCustom, monorepoType } = useYaotaiGlobalContextState();
  const { language } = useAntLocale();
  const [searchParams] = useSearchParams();
  const { loginRegisterType, setLoginType } = useLoginType();
  const { t } = useTranslation();
  const [jumpUrl, setJumpUrl] = useState('');
  const navigate = useNavigate();
  const { registration } = useInvokeAreaPrefix();
  const { aid = '' } = useMatchAid();
  const phoneType = JSON.parse(oem.oem_log_phone?.detail || '{}').type || LoginTypeEnum.URS;

  const ursType = searchParams.get(URS_PAGE_TYPE_SEARCH_KEY);

  const callback = useCallback(
    (username: string) => {
      setNeteaseCookieDomain(cookieDomain);
      window.location.href = `${window.location.origin}/rbac/urscheck?${QS.stringify({
        username,
        server: rbacServer,
        path: jumpUrl || `${window.location.origin}${window.location.pathname}?${searchParams.toString()}`,
      })}`;
    },
    [cookieDomain, rbacServer, jumpUrl, searchParams],
  );
  useImperativeHandle(ref, () => {
    return {
      showModal: (url?: string) => {
        setJumpUrl(url!);
        showModal();
      },
      closeModal,
    };
  });

  useDeepCompareEffect(() => {
    initUrs({
      ...(options || {}),
      regcb: (username: string) => callback(username),
      logincb: (username: string) => callback(username),
      id: URS_ID,
      page: ursType as UrsPageType,
      changepage: pageType => {
        searchParams.delete(URS_PAGE_TYPE_SEARCH_KEY);
        searchParams.append(URS_PAGE_TYPE_SEARCH_KEY, pageType);
        navigate(
          {
            search: searchParams.toString(),
          },
          { replace: true },
        );
      },
    });
  }, [loginRegisterType, language, callback, options, navigate, searchParams]);

  // eslint-disable-next-line react-func/max-lines-per-function
  useEffect(() => {
    if (commonAntModalProps.open) {
      if (autoRegisterFlagRef?.current.flag) {
        !searchParams.has('from') &&
          searchParams.append('from', `${NEED_AUTO_REGISTER}-${autoRegisterFlagRef?.current.option}`);
        navigate(
          {
            search: searchParams.toString(),
          },

          { replace: true },
        );
      }
    }
  }, [commonAntModalProps.open]);

  const logo = language.includes(EN) ? YAOTAI_LOGO_BLACK_EN : YAOTAI_LOGO_BLACK;
  const loginMap = new Map<LoginRegisterType, JSX.Element>([
    [
      LoginType.Phone,
      phoneType === LoginTypeEnum.RBAC ? (
        <PhoneLogin
          countdownTime={90}
          invokeType="modal"
          cookieDomain={cookieDomain}
          key={LoginType.Phone}
          serverCode={rbacServer}
          className="phone-modal"
          mediaType="h5"
          lang={language}
          aid={aid}
          monorepoType={monorepoType!}
          loginSuccessJumpUrl={searchParams.get('next') || window.location.href}
          getLoginMsgCodeApi={API.current!.login?.getLoginMsgCode!}
        />
      ) : (
        <div key={LoginType.Phone} className={'urs-content'} id={URS_ID}></div>
      ),
    ],
    [
      LoginType.CommonMail,
      <EmailLogin
        encryptPassword={encryptPassword}
        key={LoginType.CommonMail}
        className="modal-email-login"
        serverCode={rbacServer}
        cookieDomain={cookieDomain}
        mediaType="pc"
        invokeType="modal"
        lang={language}
        registerUrl={`${window.location.origin}/${SubPackageSlugEnum.Center}${registration}?next=${encodeNextUrl(
          searchParams.get('next')!,
        )}`}
        handleNotRegistered={() => {
          window.location.href = `${window.location.origin}/${
            SubPackageSlugEnum.Center
          }${registration}?next=${encodeNextUrl(searchParams.get('next')!)}`;
        }}
        loginSuccessJumpUrl={jumpUrl || window.location.href}
        // handleForgetModalCustomerContact={awaitYsfOpen}
      />,
    ],
  ]);
  [LoginType.NetEaseMail, MailRegisterType.UrsMail].forEach(key =>
    loginMap.set(key, <div key={LoginType.NetEaseMail} className={'urs-content'} id={URS_ID}></div>),
  );

  return (
    <Modal title="" width={520} footer={null} {...commonAntModalProps} forceRender>
      <UrsModalLayout extra={!!titleMap.get(loginRegisterType)?.(t)?.extra}>
        {oem?.oem_log_lt_logo?.status && (
          <img
            className={'logo'}
            onClick={() => {
              if (!isMainLogoOemCustom) {
                window.location.href = window.location.origin;
              }
            }}
            src={logo}
            alt=""
          />
        )}

        {titleMap.get(loginRegisterType)?.(t)?.title && (
          <div className="extra-toolbar">
            <div>{titleMap.get(loginRegisterType)?.(t)?.title}</div>
            {titleMap.get(loginRegisterType)?.(t)?.extra && (
              <a onClick={() => setLoginType(LoginType.Phone)}>{titleMap.get(loginRegisterType)?.(t)?.extra}</a>
            )}
          </div>
        )}
        {loginMap.get(loginRegisterType)}
        {loginRegisterType === LoginType.Phone && (
          <ThirdLogin
            oem={oem}
            otherLogo={OTHER_LOGIN_ICON}
            rbac_server={rbacServer}
            portHost={window.location.origin}
            rbacUrl={rbacUrl}
            overseaRbacUrl={overseaRbacUrl}
            wechatLogo={WECHAT_LOGIN_ICON}
            jumpUrl={jumpUrl || `${window.location.origin}${window.location.pathname}?${searchParams.toString()}`}
            googleLogo={GOOGLE_LOGIN_ICON}
            style={{
              margin: '10px auto 0',
            }}
          />
        )}
        {oem?.oem_copy_writing?.status && (
          <div className="yaotai-tips">{oem?.oem_copy_writing?.detail || '网易瑶台提供技术支持'}</div>
        )}
      </UrsModalLayout>
    </Modal>
  );
});
